/**
 * ClearedAnswersConfirmDialog.js
 *
 * A blocking confirmation dialog shown to the respondent when saving (or
 * editing) a gating answer would silently clear answers from other questions
 * that are not visible on the current screen. The dialog lists the affected
 * question titles and their current answers, giving the respondent a chance
 * to cancel the change.
 *
 * Built on the same Radix AlertDialog + Tailwind pattern as
 * SelfHarmCrisisSupportDialog.js (service-user surface, same folder) so that
 * overlay, motion classes, border-radius, and z-indexing are identical. The
 * only visual difference is the button pair: a gold-fill confirm ("Continue
 * and clear answers") and a ghost-secondary cancel ("Go back"), rather than
 * SelfHarmCrisisSupportDialog's single acknowledge CTA.
 *
 * Design rules applied:
 *   - Service-user surface: gold fill `#E7C22F` / plum label `#43213E` for
 *     the primary action; plum outline ghost for the cancel.
 *   - 44px minimum touch targets, sentence case, Atkinson Hyperlegible Next
 *     body, Poppins SemiBold headings (inherited from Tailwind config).
 *   - Focus states are Radix defaults (visible, ring-based).
 *   - Escape/Cancel returns to the edit with nothing saved (AlertDialog
 *     semantics: clicking outside or pressing Escape triggers Cancel).
 *   - Copy is plain English (~reading age 12). Clinical accuracy and
 *     comprehension review (Tom + Jasmine) required before release.
 *
 * Props:
 *   isOpen    — boolean controlling visibility
 *   entries   — array of { questionTitle, answer } describing what will be cleared
 *   onConfirm — callback: fold the clears into the save and proceed
 *   onCancel  — callback: discard pending save, stay on the question
 */

import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';

/**
 * Render a single answer value as a human-readable string. Handles the same
 * shapes produced by formatAnswerForDisplay in GenericFormPage: plain strings,
 * arrays of {value, otherValue?} objects, and raw objects with a .value key.
 */
function renderAnswer(answer) {
  if (answer === null || answer === undefined) return 'No answer given';
  if (typeof answer === 'string') return answer;
  if (Array.isArray(answer)) {
    return answer
      .map(item => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const label = item.value || '';
          return item.otherValue ? `${label}: ${item.otherValue}` : label;
        }
        return String(item);
      })
      .join(', ');
  }
  if (typeof answer === 'object' && answer.value !== undefined) {
    return answer.otherValue
      ? `${answer.value}: ${answer.otherValue}`
      : String(answer.value);
  }
  return String(answer);
}

export default function ClearedAnswersConfirmDialog({ isOpen, entries = [], onConfirm, onCancel }) {
  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialog.Portal>
        {/* Overlay — matches SelfHarmCrisisSupportDialog / AllFormsCompletedFeedbackModal */}
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" />

        {/* Content panel — centred, scrollable, max-w-2xl */}
        <AlertDialog.Content
          className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-[95vw] sm:w-[90vw] max-w-2xl max-h-[90vh] overflow-y-auto z-50"
        >
          {/* Title — sentence case, required for accessibility */}
          <AlertDialog.Title className="text-lg font-semibold text-[#2A1A28] mb-4">
            Some answers will be cleared
          </AlertDialog.Title>

          {/* Explanation */}
          <AlertDialog.Description className="text-[#2A1A28] text-base leading-relaxed mb-4">
            Because you changed your answer, the following question{entries.length === 1 ? '' : 's'} no
            longer {entries.length === 1 ? 'applies' : 'apply'}. If you continue,{' '}
            {entries.length === 1 ? 'this answer' : 'these answers'} will be cleared:
          </AlertDialog.Description>

          {/* List of affected questions + answers */}
          <ul className="space-y-3 mb-6">
            {entries.map((entry, idx) => (
              <li key={entry.varName || idx} className="border border-[#E6DFE6] rounded-lg p-3 bg-[#F3E9F1]">
                <p className="text-sm font-semibold text-[#2A1A28]">{entry.questionTitle}</p>
                <p className="text-sm text-[#6B4265] mt-1">
                  Your answer: {renderAnswer(entry.answer)}
                </p>
              </li>
            ))}
          </ul>

          {/* Action buttons — gold confirm (primary), ghost cancel (secondary) */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            {/* Cancel uses Radix AlertDialog.Cancel which triggers onOpenChange(false)
                and routes through to onCancel. No separate onClick needed — adding
                one would double-fire the callback. */}
            <AlertDialog.Cancel asChild>
              <button
                className="inline-flex items-center justify-center px-6 py-3 min-h-[44px] rounded-lg border-2 border-[#43213E] text-[#43213E] font-semibold text-base bg-transparent transition-colors hover:bg-[#F3E9F1] focus:outline-none focus:ring-2 focus:ring-[#43213E] focus:ring-offset-2"
              >
                Go back
              </button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <button
                onClick={onConfirm}
                className="inline-flex items-center justify-center px-6 py-3 min-h-[44px] rounded-lg bg-[#E7C22F] text-[#43213E] font-semibold text-base transition-colors hover:bg-[#d4b129] focus:outline-none focus:ring-2 focus:ring-[#43213E] focus:ring-offset-2"
              >
                Continue and clear {entries.length === 1 ? 'answer' : 'answers'}
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
